import { signal } from '@preact/signals-core';
import { activeRoleSignal, customersPageMetaSignal, customersSignal, getAuthHeaders } from './assets';
import { showToast } from '../utils/toast';
import { createDebouncedRunner } from '../utils/debounce';
import { escapeHtml } from '../utils/sanitize';
import type { components } from '../api/v1';

type Customer = components['schemas']['Customer'];
type CustomersIndexResponse = {
	items: Customer[];
	page: number;
	pageSize: number;
	total: number;
};

export const selectedCustomerIdSignal = signal<string | null>(null);
export const customersSearchQuerySignal = signal<string>('');
export const customersPageSignal = signal<number>(1);
export const customersPageSizeSignal = signal<number>(20);
export const customersLoadingSignal = signal<boolean>(false);
const runCustomerSearch = createDebouncedRunner(350);
let customersFetchSeq = 0;

export async function refreshCustomers() {
	const fetchSeq = ++customersFetchSeq;
	const page = customersPageSignal.value;
	const pageSize = customersPageSizeSignal.value;
	const search = customersSearchQuerySignal.value.trim();
	const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
	if (search) params.set('search', search);

	setCustomersLoading(true);
	try {
		const res = await fetch(`/api/v1/customers/index?${params.toString()}`, { headers: getAuthHeaders() });
		if (!res.ok) return;
		const result = await res.json() as CustomersIndexResponse;
		if (fetchSeq !== customersFetchSeq) return;
		customersSignal.value = result.items;
		customersPageMetaSignal.value = { page: result.page, pageSize: result.pageSize, total: result.total };
		await clampCustomersPage();
	} catch (err) {
		console.error('Failed to fetch customer page', err);
	} finally {
		if (fetchSeq === customersFetchSeq) {
			setCustomersLoading(false);
		}
	}
}

async function clampCustomersPage() {
	const meta = customersPageMetaSignal.value;
	const totalPages = Math.max(1, Math.ceil((meta.total || 0) / meta.pageSize));
	if (customersPageSignal.value <= totalPages) return;

	customersPageSignal.value = totalPages;
	await refreshCustomers();
}

export function renderCustomersPage(): string {
	const role = activeRoleSignal.peek();
	const isReadOnly = role === 'technician' || role === 'viewer';
	const isAdminPlus = role === 'superadmin' || role === 'admin';

	const allCustomers = customersSignal.peek() || [];
	const filteredCustomers = allCustomers;
	const meta = customersPageMetaSignal.peek();
	const isLoading = customersLoadingSignal.peek();
	const totalPages = Math.max(1, Math.ceil((meta.total || 0) / meta.pageSize));

	const tableRowsHtml = filteredCustomers.map(c => {
		return `
			<s-table-row>
				<s-table-cell><strong>${escapeHtml(c.name)}</strong></s-table-cell>
				<s-table-cell>
						${!isReadOnly ? `
							<s-button id="btn-edit-customer-${c.id}" size="slim">Edit</s-button>
							<s-button id="btn-delete-customer-${c.id}" tone="critical" size="slim">Delete</s-button>
						` : `<s-text tone="subdued">Read-only</s-text>`}
				</s-table-cell>
			</s-table-row>
		`;
	}).join('');

	return `
		<s-page heading="Customer Manager" subtitle="Manage client organizations and multi-tenant accounts." inlineSize="large">
			${!isReadOnly ? `
				<div slot="primaryAction" style="display: flex; gap: 0.5rem; align-items: center;">
					<s-button variant="primary" id="btn-open-create-customer">Add Customer</s-button>
${isAdminPlus ? `
					<s-button variant="secondary" id="btn-open-import-customers">Import CSV</s-button>
					<s-button variant="secondary" href ="#customer-merge" >Merge Customers</s-button>
`: ''}
				</div>
			` : ''}

			<!-- Search Filter -->
			<s-section>
				<s-box padding="base" background="surface" border="base" borderRadius="base">
					<s-stack gap="base">

						<s-text-field 
							label="Search Customers" 
							id="customer-search-input" 
							value="${escapeHtml(customersSearchQuerySignal.peek())}"
							placeholder="Search by customer name or ID..."
							name="search"
						></s-text-field>
					</s-stack>
				</s-box>
			</s-section>

			<!-- Customers Table -->
			<s-section>
				<s-box id="customers-table-region" padding="none" background="surface" border="base" borderRadius="base" style="position:relative;display:block;">
					${renderCustomersTableRegionHtml(filteredCustomers, tableRowsHtml, meta, totalPages, isLoading)}
				</s-box>
			</s-section>

			<!-- Create Customer Modal -->
			<s-modal id="create-customer-modal" heading="Create New Customer">
				<s-form id="form-create-customer">
					<s-stack gap="base">
						<s-text-field label="Customer Name" name="name" placeholder="e.g. Acme Corp" required></s-text-field>
						<div id="create-customer-error" style="display:none; color:var(--p-color-text-critical); margin-top:0.5rem; font-size:0.875rem;"></div>
						<s-stack direction="inline" gap="small" style="margin-top:1.5rem;">
							<s-button variant="primary" id="btn-submit-create-customer">Create</s-button>
							<s-button commandFor="create-customer-modal" command="--hide">Cancel</s-button>
						</s-stack>
					</s-stack>
				</s-form>
			</s-modal>

			<!-- Edit Customer Modal -->
			<s-modal id="edit-customer-modal" heading="Edit Customer Details">
				<s-form id="form-edit-customer">
					<s-stack gap="base">
						<s-text-field label="Customer Name" name="name" id="edit-customer-name-input" required></s-text-field>
						<div id="edit-customer-error" style="display:none; color:var(--p-color-text-critical); margin-top:0.5rem; font-size:0.875rem;"></div>
						<s-stack direction="inline" gap="small" style="margin-top:1.5rem;">
							<s-button variant="primary" id="btn-submit-edit-customer">Save Changes</s-button>
							<s-button commandFor="edit-customer-modal" command="--hide">Cancel</s-button>
						</s-stack>
					</s-stack>
				</s-form>
			</s-modal>

			<!-- Delete Confirmation Modal -->
			<s-modal id="delete-customer-modal" heading="Delete Customer?">
				<s-stack gap="base">
					<s-text tone="critical">Are you sure you want to delete this customer? This action is permanent and will soft-delete the record.</s-text>
					<div id="delete-customer-error" style="display:none; color:var(--p-color-text-critical); margin-top:0.5rem; font-size:0.875rem;"></div>
					<s-stack direction="inline" gap="small" style="margin-top:1.5rem;">
						<s-button variant="primary" tone="critical" id="btn-confirm-delete-customer">Delete Customer</s-button>
						<s-button commandFor="delete-customer-modal" command="--hide">Cancel</s-button>
					</s-stack>
				</s-stack>
			</s-modal>

			<!-- Import Customers Modal -->
			<s-modal id="import-customers-modal" heading="Import Customers CSV">
				<s-stack gap="base">
					<s-text tone="subdued">
						Upload a CSV file containing customer records. The file must include a header row.
					</s-text>
					<s-box padding="base" background="surface-secondary" border="base" borderRadius="base">
						<s-text style="font-family: monospace;">name<br>Acme Corporation<br>Globex Corp</s-text>
					</s-box>
					<s-stack direction="inline" gap="small" style="align-items: center;">
						<s-button id="btn-select-customers-file">Select CSV File</s-button>
						<s-text id="import-customers-filename" tone="subdued">No file chosen</s-text>
					</s-stack>
					<input type="file" id="input-customers-csv" accept=".csv" style="display: none;" />
					<div id="import-customers-error" style="display:none; color:var(--p-color-text-critical); margin-top:0.5rem; font-size:0.875rem;"></div>
					<s-stack direction="inline" gap="small" style="margin-top:1.5rem;">
						<s-button variant="primary" id="btn-submit-import-customers">Upload & Import</s-button>
						<s-button commandFor="import-customers-modal" command="--hide">Cancel</s-button>
					</s-stack>
				</s-stack>
			</s-modal>
		</s-page>
	`;
}

export function setupCustomersPageListeners() {
	updateCustomersTableRegion();

	// 1. Search Input Listener (using onInput for live filter)
	const searchInput = document.getElementById('customer-search-input');
	if (searchInput) {
		searchInput.addEventListener('input', (e: any) => {
			customersSearchQuerySignal.value = e.target.value;
			customersPageSignal.value = 1;
			runCustomerSearch(async () => {
				await refreshCustomers();
				updateCustomersTableRegion();
			});
		});
	}

	const prevPageBtn = document.getElementById('customers-prev-page');
	if (prevPageBtn) {
		prevPageBtn.addEventListener('click', async () => {
			customersPageSignal.value = Math.max(1, customersPageSignal.value - 1);
			await refreshCustomers();
			updateCustomersTableRegion();
		});
	}

	const nextPageBtn = document.getElementById('customers-next-page');
	if (nextPageBtn) {
		nextPageBtn.addEventListener('click', async () => {
			customersPageSignal.value = Math.min(Math.max(1, customersPageSignal.value + 1), Math.max(1, Math.ceil((customersPageMetaSignal.value.total || 0) / customersPageMetaSignal.value.pageSize)));
			await refreshCustomers();
			updateCustomersTableRegion();
		});
	}

	// 2. Open Create Modal
	const openCreateBtn = document.getElementById('btn-open-create-customer');
	if (openCreateBtn) {
		openCreateBtn.addEventListener('click', () => {
			const modal = document.getElementById('create-customer-modal') as any;
			const errDiv = document.getElementById('create-customer-error');
			if (errDiv) errDiv.style.display = 'none';
			if (modal && typeof modal.showOverlay === 'function') {
				modal.showOverlay();
			}
		});
	}

	// 3. Submit Create Form
	const submitCreateBtn = document.getElementById('btn-submit-create-customer');
	if (submitCreateBtn) {
		submitCreateBtn.addEventListener('click', async (e) => {
			e.preventDefault();
			const form = document.getElementById('form-create-customer');
			if (!form) return;
			const name = (form.querySelector('[name="name"]') as any)?.value || '';

			const errDiv = document.getElementById('create-customer-error');
			if (errDiv) errDiv.style.display = 'none';

			try {
				const res = await fetch('/api/v1/customers', {
					method: 'POST',
					headers: getAuthHeaders(),
					body: JSON.stringify({ name }),
				});

				if (res.ok) {
					const modal = document.getElementById('create-customer-modal') as any;
					if (modal && typeof modal.hideOverlay === 'function') {
						modal.hideOverlay();
					}
					await refreshCustomers();
					updateCustomersTableRegion();
					// Trigger standard dev-customer-switcher update by forcing total redraw of main.ts
					triggerGlobalStateSync();
				} else {
					const data = await res.json();
					if (errDiv) {
						errDiv.textContent = data.error || 'Failed to create customer.';
						errDiv.style.display = 'block';
					}
				}
			} catch (err) {
				if (errDiv) {
					errDiv.textContent = 'Network failure.';
					errDiv.style.display = 'block';
				}
			}
		});
	}

	// 4. Attach Row Edit & Delete Listeners
	(customersSignal.value || []).forEach(c => {
		const editBtn = document.getElementById(`btn-edit-customer-${c.id}`);
		if (editBtn) {
			editBtn.addEventListener('click', () => {
				selectedCustomerIdSignal.value = c.id;
				const nameInput = document.getElementById('edit-customer-name-input') as HTMLInputElement;
				if (nameInput) nameInput.value = c.name;

				const errDiv = document.getElementById('edit-customer-error');
				if (errDiv) errDiv.style.display = 'none';

				const modal = document.getElementById('edit-customer-modal') as any;
				if (modal && typeof modal.showOverlay === 'function') {
					modal.showOverlay();
				}
			});
		}

		const deleteBtn = document.getElementById(`btn-delete-customer-${c.id}`);
		if (deleteBtn) {
			deleteBtn.addEventListener('click', () => {
				selectedCustomerIdSignal.value = c.id;
				const errDiv = document.getElementById('delete-customer-error');
				if (errDiv) errDiv.style.display = 'none';

				const modal = document.getElementById('delete-customer-modal') as any;
				if (modal && typeof modal.showOverlay === 'function') {
					modal.showOverlay();
				}
			});
		}
	});

	// 5. Submit Edit Form
	const submitEditBtn = document.getElementById('btn-submit-edit-customer');
	if (submitEditBtn) {
		submitEditBtn.addEventListener('click', async (e) => {
			e.preventDefault();
			const id = selectedCustomerIdSignal.value;
			if (!id) return;

			const form = document.getElementById('form-edit-customer');
			if (!form) return;
			const name = (form.querySelector('[name="name"]') as any)?.value || '';

			const errDiv = document.getElementById('edit-customer-error');
			if (errDiv) errDiv.style.display = 'none';

			try {
				const res = await fetch(`/api/v1/customers/${id}`, {
					method: 'PUT',
					headers: getAuthHeaders(),
					body: JSON.stringify({ name }),
				});

				if (res.ok) {
					const modal = document.getElementById('edit-customer-modal') as any;
					if (modal && typeof modal.hideOverlay === 'function') {
						modal.hideOverlay();
					}
					await refreshCustomers();
					updateCustomersTableRegion();
					triggerGlobalStateSync();
				} else {
					const data = await res.json();
					if (errDiv) {
						errDiv.textContent = data.error || 'Failed to update customer.';
						errDiv.style.display = 'block';
					}
				}
			} catch (err) {
				if (errDiv) {
					errDiv.textContent = 'Network failure.';
					errDiv.style.display = 'block';
				}
			}
		});
	}

	// 6. Confirm Delete Button
	const confirmDeleteBtn = document.getElementById('btn-confirm-delete-customer');
	if (confirmDeleteBtn) {
		confirmDeleteBtn.addEventListener('click', async () => {
			const id = selectedCustomerIdSignal.value;
			if (!id) return;

			const errDiv = document.getElementById('delete-customer-error');
			if (errDiv) errDiv.style.display = 'none';

			try {
				const res = await fetch(`/api/v1/customers/${id}`, {
					method: 'DELETE',
					headers: getAuthHeaders(),
				});

				if (res.ok) {
					const modal = document.getElementById('delete-customer-modal') as any;
					if (modal && typeof modal.hideOverlay === 'function') {
						modal.hideOverlay();
					}
					await refreshCustomers();
					updateCustomersTableRegion();
					triggerGlobalStateSync();
				} else {
					const data = await res.json();
					if (errDiv) {
						errDiv.textContent = data.error || 'Failed to delete customer.';
						errDiv.style.display = 'block';
					}
				}
			} catch (err) {
				if (errDiv) {
					errDiv.textContent = 'Network failure.';
					errDiv.style.display = 'block';
				}
			}
		});
	}

	// 7. Import CSV Open Button
	const openImportBtn = document.getElementById('btn-open-import-customers');
	if (openImportBtn) {
		openImportBtn.addEventListener('click', () => {
			const modal = document.getElementById('import-customers-modal') as any;
			const errDiv = document.getElementById('import-customers-error');
			if (errDiv) errDiv.style.display = 'none';
			const filenameText = document.getElementById('import-customers-filename');
			if (filenameText) filenameText.textContent = 'No file chosen';
			const fileInput = document.getElementById('input-customers-csv') as HTMLInputElement;
			if (fileInput) fileInput.value = '';
			if (modal && typeof modal.showOverlay === 'function') {
				modal.showOverlay();
			}
		});
	}

	// 8. Select CSV File Buttons
	const selectFileBtn = document.getElementById('btn-select-customers-file');
	const fileInput = document.getElementById('input-customers-csv') as HTMLInputElement;
	if (selectFileBtn && fileInput) {
		selectFileBtn.addEventListener('click', () => {
			fileInput.click();
		});
		fileInput.addEventListener('change', () => {
			const filenameText = document.getElementById('import-customers-filename');
			if (filenameText) {
				filenameText.textContent = fileInput.files && fileInput.files[0] ? fileInput.files[0].name : 'No file chosen';
			}
		});
	}

	// 9. Submit CSV Upload Form
	const submitImportBtn = document.getElementById('btn-submit-import-customers');
	if (submitImportBtn) {
		submitImportBtn.addEventListener('click', async (e) => {
			e.preventDefault();
			const fileInput = document.getElementById('input-customers-csv') as HTMLInputElement;
			const errDiv = document.getElementById('import-customers-error');
			if (errDiv) errDiv.style.display = 'none';

			if (!fileInput || !fileInput.files || fileInput.files.length === 0) {
				if (errDiv) {
					errDiv.textContent = 'Please select a CSV file first.';
					errDiv.style.display = 'block';
				}
				return;
			}

			const file = fileInput.files[0];
			if (!file) return;
			const formData = new FormData();
			formData.append('file', file);

			try {
				const res = await fetch('/api/v1/customers/import', {
					method: 'POST',
					headers: {
						'Authorization': getAuthHeaders()['Authorization'] || '',
					},
					body: formData,
				});

				if (res.ok) {
					const data = await res.json();
					const modal = document.getElementById('import-customers-modal') as any;
					if (modal && typeof modal.hideOverlay === 'function') {
						modal.hideOverlay();
					}
					customersPageSignal.value = 1;
					showToast(`Imported ${data.importedCount} and skipped ${data.updatedCount} customers successfully!`, 'success');
					await refreshCustomers();
					updateCustomersTableRegion();
					triggerGlobalStateSync();
				} else {
					const data = await res.json();
					if (errDiv) {
						errDiv.textContent = data.error || 'Failed to import CSV.';
						errDiv.style.display = 'block';
					}
				}
			} catch (err) {
				if (errDiv) {
					errDiv.textContent = 'Network failure.';
					errDiv.style.display = 'block';
				}
			}
		});
	}
}

function renderCustomersTableRegionHtml(
	filteredCustomers: Customer[],
	tableRowsHtml: string,
	meta: { page: number; pageSize: number; total: number },
	totalPages: number,
	isLoading: boolean
): string {
	const loadingOverlayHtml = renderCustomersLoadingOverlayHtml(isLoading);

	if (filteredCustomers.length === 0) {
		return `
			<div style="padding: 3rem; text-align: center;">
				<s-text tone="subdued">No customers found.</s-text>
			</div>
			${loadingOverlayHtml}
		`;
	}

	return `
		<s-table>
			<s-table-header-row>
				<s-table-header>Customer Name</s-table-header>
				<s-table-header>Actions</s-table-header>
			</s-table-header-row>
			<s-table-body>
				${tableRowsHtml}
			</s-table-body>
		</s-table>
		<s-stack direction="inline" gap="small" style="justify-content: space-between; align-items: center; padding: 1rem;">
			<s-text tone="subdued">Page ${meta.page} of ${totalPages} · ${meta.total} customers</s-text>
			<div style="display:flex; gap:0.5rem;">
				<s-button id="customers-prev-page" size="slim" ${meta.page <= 1 ? 'disabled' : ''}>Previous</s-button>
				<s-button id="customers-next-page" size="slim" ${meta.page >= totalPages ? 'disabled' : ''}>Next</s-button>
			</div>
		</s-stack>
		${loadingOverlayHtml}
	`;
}

function renderCustomersLoadingOverlayHtml(isLoading: boolean): string {
	return `
		<div id="customers-loading-spinner" style="display:${isLoading ? 'flex' : 'none'}; position:absolute; inset:0; align-items:center; justify-content:center; background:rgba(255,255,255,0.72); z-index:10; border-radius:4px;">
			<s-stack gap="small" align="center">
				<s-spinner size="large"></s-spinner>
				<s-text tone="subdued">Loading customers...</s-text>
			</s-stack>
		</div>
	`;
}

function updateCustomersTableRegion() {
	const region = document.getElementById('customers-table-region');
	if (!region) return;

	const role = activeRoleSignal.value;
	const isReadOnly = role === 'technician' || role === 'viewer';
	const allCustomers = customersSignal.value || [];
	const meta = customersPageMetaSignal.value;
	const isLoading = customersLoadingSignal.value;
	const totalPages = Math.max(1, Math.ceil((meta.total || 0) / meta.pageSize));
	const tableRowsHtml = allCustomers.map(c => {
		return `
			<s-table-row>
				<s-table-cell><strong>${escapeHtml(c.name)}</strong></s-table-cell>
				<s-table-cell>
						${!isReadOnly ? `
							<s-button id="btn-edit-customer-${c.id}" size="slim">Edit</s-button>
							<s-button id="btn-delete-customer-${c.id}" tone="critical" size="slim">Delete</s-button>
						` : `<s-text tone="subdued">Read-only</s-text>`}
				</s-table-cell>
			</s-table-row>
		`;
	}).join('');

	region.innerHTML = renderCustomersTableRegionHtml(allCustomers, tableRowsHtml, meta, totalPages, isLoading);
	bindCustomerRowListeners(allCustomers);
	bindCustomerPaginationListeners();
}

function setCustomersLoading(isLoading: boolean) {
	customersLoadingSignal.value = isLoading;
	updateCustomersLoadingOverlay();
}

function updateCustomersLoadingOverlay() {
	const spinner = document.getElementById('customers-loading-spinner');
	if (!spinner) return;
	spinner.style.display = customersLoadingSignal.value ? 'flex' : 'none';
}

function bindCustomerRowListeners(allCustomers: Customer[]) {
	const role = activeRoleSignal.value;
	const isReadOnly = role === 'technician' || role === 'viewer';

	allCustomers.forEach(c => {
		const editBtn = document.getElementById(`btn-edit-customer-${c.id}`);
		if (editBtn && !isReadOnly) {
			editBtn.addEventListener('click', () => {
				selectedCustomerIdSignal.value = c.id;
				const nameInput = document.getElementById('edit-customer-name-input') as HTMLInputElement;
				if (nameInput) nameInput.value = c.name;

				const errDiv = document.getElementById('edit-customer-error');
				if (errDiv) errDiv.style.display = 'none';

				const modal = document.getElementById('edit-customer-modal') as any;
				if (modal && typeof modal.showOverlay === 'function') {
					modal.showOverlay();
				}
			});
		}

		const deleteBtn = document.getElementById(`btn-delete-customer-${c.id}`);
		if (deleteBtn && !isReadOnly) {
			deleteBtn.addEventListener('click', () => {
				selectedCustomerIdSignal.value = c.id;
				const errDiv = document.getElementById('delete-customer-error');
				if (errDiv) errDiv.style.display = 'none';

				const modal = document.getElementById('delete-customer-modal') as any;
				if (modal && typeof modal.showOverlay === 'function') {
					modal.showOverlay();
				}
			});
		}
	});
}

function bindCustomerPaginationListeners() {
	const prevPageBtn = document.getElementById('customers-prev-page');
	if (prevPageBtn) {
		prevPageBtn.addEventListener('click', async () => {
			customersPageSignal.value = Math.max(1, customersPageSignal.value - 1);
			await refreshCustomers();
			updateCustomersTableRegion();
		});
	}

	const nextPageBtn = document.getElementById('customers-next-page');
	if (nextPageBtn) {
		nextPageBtn.addEventListener('click', async () => {
			customersPageSignal.value = Math.min(Math.max(1, customersPageSignal.value + 1), Math.max(1, Math.ceil((customersPageMetaSignal.value.total || 0) / customersPageMetaSignal.value.pageSize)));
			await refreshCustomers();
			updateCustomersTableRegion();
		});
	}
}

function triggerGlobalStateSync() {
	// Reassigning customersSignal will trigger any observers on the sidebar switcher
	customersSignal.value = [...customersSignal.value];
}

export function unmountCustomersPage() {}
